import {
    fetchLpStateDataIds,
    heatmapDataIdKey,
    heatmapDomChangeDataIdKey,
    heatmapClickClass,
    heatmapAttentionClass,
    getHeatmapDomChangeObserveElements,
    setHeatmapAttribute,
    pickLpStateKeyValue,
    roundCalc,
    queryStringMap,
    isVisibilityElement,
    DOM_STABLE_DURATION,
    DOM_INIT_FORCE_STANDBY_MS,
    HEATMAP_RENDER_DEBOUNCE_MS,
    HEATMAP_DEFAULT_ORIGIN,
    HEATMAP_PREVIEW_WS,
    ALLOWED_ORIGINS,
    HEATMAP_PREVIEW_HASH_HEIGHT,
    getPageHeight,
    isFixed,
    heatmapArrivalClass,
    DOM_CHANGE_DEBOUNCE_MS,
    isAttentionCandidateElement,
    getAttentionVisibleRect,
    getAttentionDataId,
    hasHeatmapContentClassChanged,
    sha1,
} from './heatmap-common.js';

const CLICK_COUNT_INTERACTIVE_SELECTOR = 'a[href],button,label,input:not([type="hidden"]),select,textarea,[role="button"],[role="link"]';
const CLICK_COUNT_TARGET_SELECTOR = `${CLICK_COUNT_INTERACTIVE_SELECTOR},img,video`;
const CLICK_COUNT_MEDIA_MIN_WIDTH = 80;
const CLICK_COUNT_MEDIA_MIN_HEIGHT = 50;

// Keep global point coordinates in the engine store: shifting the input points
// could change its key iteration/compositing order around a tile boundary.
function createPreviewTileLayer(engine, config, getViewport, documentRoot = document) {
  const tileHeight = 1024;
  // Match the original engine's positioning of its containing block.
  config.container.style.position = 'relative';
  const root = documentRoot.createElement('div');
  root.className = 'heatmap-preview-tile-layer';
  Object.assign(root.style, {position: 'absolute', left: '0', top: '0',
    margin: '0', padding: '0', border: '0', boxSizing: 'border-box',
    pointerEvents: 'none', zIndex: '289999', overflow: 'hidden',
    backgroundColor: config.backgroundColor || 'transparent'});
  config.container.appendChild(root);
  const tiles = new Map();
  let width = 0, height = 0;
  return {
    resize(nextWidth, nextHeight) {
      nextWidth = Math.max(1, Math.floor(nextWidth));
      nextHeight = Math.max(1, Math.floor(nextHeight));
      if (width !== nextWidth) { width = nextWidth; root.style.width = `${width}px`; }
      if (height !== nextHeight) { height = nextHeight; root.style.height = `${height}px`; }
    },
    setData(data) {
      const viewport = getViewport();
      const span = Math.max(0, viewport.bottom - viewport.top);
      // One screen of overscan on either side; retained bitmap memory is bounded
      // by the visible region rather than by the full LP height.
      const start = Math.max(0, Math.floor((viewport.top - span) / tileHeight));
      const end = span === 0 ? start : Math.min(Math.ceil(height / tileHeight), Math.ceil((viewport.bottom + span) / tileHeight));
      for (const [index, tile] of tiles) {
        if (index < start || index >= end || tile.width !== width || tile.height !== Math.min(tileHeight, height - index * tileHeight)) {
          tile.container.remove();
          tiles.delete(index);
        }
      }
      for (let index = start; index < end; index++) {
        const top = index * tileHeight;
        const h = Math.min(tileHeight, height - top);
        // Include every kernel that touches this tile, including neighbouring
        // points outside its bounds. Use the original global normalization.
        const points = data.data.filter(point => {
          const padding = config.c_height ? Math.ceil(config.c_height / 2) + 1 : (point.radius || config.radius || 40);
          return point.y + padding >= top && point.y - padding < top + h;
        });
        let tile = tiles.get(index);
        if (!points.length) {
          if (tile) { tile.container.remove(); tiles.delete(index); }
          continue;
        }
        const signature = JSON.stringify([data.min, data.max, points]);
        if (tile?.signature === signature) continue;
        if (!tile) {
          const container = documentRoot.createElement('div');
          container.className = 'heatmap-preview-tile';
          Object.assign(container.style, {position: 'absolute', top: `${top}px`, left: '0',
            margin: '0', padding: '0', border: '0', boxSizing: 'border-box',
            width: `${width}px`, height: `${h}px`, overflow: 'hidden', pointerEvents: 'none'});
          root.appendChild(container);
          const instance = engine.create({...config, container, width: config.width || width,
            height: h, offsetY: top, backgroundColor: 'rgba(0,0,0,0)'});
          // The engine sets its container to relative; tiles must stay at their
          // absolute document offsets rather than stacking in normal flow.
          container.style.position = 'absolute';
          // Attention retains its existing horizontal resolution and CSS scaling.
          if (config.width) {
            const canvas = container.querySelector('.heatmap-canvas');
            if (canvas) { canvas.style.transformOrigin = '0 0'; canvas.style.transform = `scaleX(${width / config.width})`; }
          }
          tile = {container, instance, width, height: h};
          tiles.set(index, tile);
        }
        tile.instance.setData({...data, data: points});
        tile.signature = signature;
      }
    },
    destroy() { root.remove(); tiles.clear(); },
  };
}

function isClickCountTarget(element, rect) {
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (element.tagName === 'IMG' || element.tagName === 'VIDEO') {
    return rect.width >= CLICK_COUNT_MEDIA_MIN_WIDTH && rect.height >= CLICK_COUNT_MEDIA_MIN_HEIGHT;
  }
  return element.matches(CLICK_COUNT_INTERACTIVE_SELECTOR);
}

// LPのレイアウトを変えず、固定・追従要素にも合わせてクリック数を表示する。
function createClickCountOverlay({dataIdKey, isVisible, documentRoot = document, windowRoot = window}) {
  const container = documentRoot.createElement('div');
  container.className = 'heatmap-preview-click-count-container';
  container.setAttribute('aria-hidden', 'true');
  documentRoot.body.appendChild(container);

  let enabled = false;
  let counts = {};
  let frame = null;

  const render = () => {
    if (!enabled) return;
    const fragment = documentRoot.createDocumentFragment();
    for (const element of documentRoot.querySelectorAll(CLICK_COUNT_TARGET_SELECTOR)) {
      const count = Number(counts[element.getAttribute(dataIdKey)] ?? 0);
      if (!Number.isFinite(count) || count <= 0) continue;
      const rect = element.getBoundingClientRect();
      if (!isClickCountTarget(element, rect) || !isVisible(element)) continue;
      if (rect.bottom <= 0 || rect.top >= windowRoot.innerHeight || rect.right <= 0 || rect.left >= windowRoot.innerWidth) continue;

      const badge = documentRoot.createElement('span');
      badge.className = 'heatmap-preview-click-count';
      badge.textContent = String(count);
      badge.style.setProperty('left', `${Math.min(windowRoot.innerWidth - 2, Math.max(24, rect.right - 2))}px`, 'important');
      badge.style.setProperty('top', `${Math.max(2, rect.top + 2)}px`, 'important');
      fragment.appendChild(badge);
    }
    container.replaceChildren(fragment);
  };

  const scheduleRender = () => {
    if (!enabled || frame !== null) return;
    frame = windowRoot.requestAnimationFrame(() => {
      frame = null;
      render();
    });
  };
  documentRoot.addEventListener('scroll', scheduleRender, {passive: true, capture: true});
  windowRoot.addEventListener('resize', scheduleRender, {passive: true});

  return {
    update(nextCounts, show) {
      counts = nextCounts ?? {};
      enabled = show;
      if (frame !== null) {
        windowRoot.cancelAnimationFrame(frame);
        frame = null;
      }
      if (enabled) render();
      else container.replaceChildren();
    },
  };
}

export const heatmapPreview = async() => {
    //ヒートマップ下の要素のイベントを発火させるための設定
    (() => {
        let style = `
        <style>
        .heatmap-preview-click-count-container {
            all: initial !important;
            position: fixed !important;
            inset: 0 !important;
            z-index: 399999 !important;
            pointer-events: none !important;
            overflow: hidden !important;
        }
        .heatmap-preview-click-count {
            all: initial !important;
            position: absolute !important;
            pointer-events: none !important;
            box-sizing: border-box !important;
            padding: 1px 4px !important;
            border-radius: 10px !important;
            color: #f2f2f2 !important;
            background: #323232e0 !important;
            box-shadow: 0 8px 16px #00000073 !important;
            font: 400 10px/14px sans-serif !important;
            white-space: nowrap !important;
            transform: translateX(-100%) !important;
        }
        .loader-wrap {
            opacity: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            width: 100%;
            position: fixed;
            top: 0;
            left: 0;
            pointer-events: none;
            z-index: 23456789;
        }
        .loader,
        .loader:after {
            border-radius: 50%;
            width: 10em;
            height: 10em;
        }
        .loader {
            font-size: 10px;
            position: relative;
            text-indent: -9999em;
            border-top: 1.1em solid rgba(255, 255, 255, 0.2);
            border-right: 1.1em solid rgba(255, 255, 255, 0.2);
            border-bottom: 1.1em solid rgba(255, 255, 255, 0.2);
            border-left: 1.1em solid #21ffe2;
            -webkit-transform: translateZ(0);
            -ms-transform: translateZ(0);
            transform: translateZ(0);
            -webkit-animation: load8 1.1s infinite linear;
            animation: load8 1.1s infinite linear;
        }
        @-webkit-keyframes load8 {
            0% {
                -webkit-transform: rotate(0deg);
                transform: rotate(0deg);
            }
            100% {
                -webkit-transform: rotate(360deg);
                transform: rotate(360deg);
            }
        }
        @keyframes load8 {
            0% {
                -webkit-transform: rotate(0deg);
                transform: rotate(0deg);
            }
            100% {
                -webkit-transform: rotate(360deg);
                transform: rotate(360deg);
            }
        }

        .heatmap-canvas {
          pointer-events: none;
          z-index: 289999;
        }

        .heatmap-preview-attention-second-container {
            position: absolute;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
            z-index: 299999;
            pointer-events: none;
            overflow: hidden;
        }

        .heatmap-preview-attention-second {
            position: absolute;
            border-bottom: 2px #333333 dotted;
            width: 100%;
            box-sizing: border-box;
            top: 0;
            margin: 0 !important;
            padding: 0 !important;
        }

        .heatmap-preview-attention-second-value {
            color: #fff;
            background: #0e70a9;
            position: absolute;
            display: inline-block;
            left: 0;
            top: -19px;
            height: 20px;
            width: 65px;
            font-size: 14px;
            text-align: center;
            line-height: 1.5;
        }

        .heatmap-preview-arrival-container {
            position: absolute;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
            z-index: 299999;
            pointer-events: none;
            overflow: hidden;
        }

        .heatmap-preview-arrival {
            position: absolute;
            color: #fff;
            border-bottom: 2px #333333 dotted;
            width: 100%;
            box-sizing: border-box;
            top: 0;
            margin: 0 !important;
            padding: 0 !important;
        }

        .heatmap-preview-arrival-per {
            color: #fff;
            background: #0d5d8a;
            position: absolute;
            display: inline-block;
            left: 65px;
            top: -19px;
            height: 20px;
            width: 65px;
            font-size: 14px;
            text-align: center;
            line-height: 1.5;
        }

        .heatmap-preview-arrival-transition-container {
            position: absolute;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
            z-index: 299999;
            pointer-events: none;
            overflow: hidden;
        }

        .heatmap-preview-arrival-transition {
            position: absolute;
            color: #fff;
            border-bottom: 2px #333333 dotted;
            width: 100%;
            box-sizing: border-box;
            top: 0;
            margin: 0 !important;
            padding: 0 !important;
        }

        .heatmap-preview-arrival-transition-per {
            color: #fff;
            background: #0e4767;
            position: absolute;
            display: inline-block;
            left: 130px;
            top: -19px;
            height: 20px;
            width: 65px;
            font-size: 14px;
            text-align: center;
            line-height: 1.5;
        }

        .heatmap-preview-leave-container {
            position: absolute;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
            z-index: 299999;
            pointer-events: none;
            overflow: hidden;
        }

        .heatmap-preview-leave {
            position: absolute;
            color: #fff;
            border-bottom: 2px #333333 dotted;
            width: 100%;
            box-sizing: border-box;
            top: 0;
            margin: 0 !important;
            padding: 0 !important;
        }

        .heatmap-preview-leave-per {
            color: #fff;
            background: #082d42;
            position: absolute;
            display: inline-block;
            left: 195px;
            top: -19px;
            height: 20px;
            width: 65px;
            font-size: 14px;
            text-align: center;
            line-height: 1.5;
        }

        .heatmap-preview-leave-title {
            border: none;
        }

        .heatmap-preview-fixed-container {
            position: fixed !important;
            height: 100vh;
            width: 100vw;
            left: 0;
            top: 0;
            z-index: 299998;
            pointer-events: none;
        }

        .heatmap-tooltip {
            position: fixed !important;
            top: -100vh;
            left: -100vw;
            transform: translate(-50%, -100%);
            padding: 4px 16px;
            font-size: 10px;
            color: #f2f2f2;
            background: #323232e0;
            border-radius: 10px;
            box-shadow: 0 8px 16px #00000073;
            white-space: nowrap;
            z-index: 999999;
            pointer-events: none;
            transform: translate(-50%, -50%);
            opacity: 0;
            transition: opacity 0.3s ease-out;
        }
        .heatmap-tooltip.is-show {
          opacity: 1;
        }

        /* スクロールバー非表示 */
        html, body {
          /* Firefox / IE 系 */
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
        }

        /* Chromium / WebKit */
        html::-webkit-scrollbar,
        body::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        </style>`;

        document.querySelector(`head`).insertAdjacentHTML('beforeend', style);
        document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-preview-attention-second-container"></div>`);
        document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-preview-arrival-container"></div>`);
        document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-preview-arrival-transition-container"></div>`);
        document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-preview-leave-container"></div>`);
        document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-tooltip"></div>`);
        
    })();

    const currentUrl = new URL(location.href);
    const currentPathname = currentUrl.pathname.replace(/\/index\.(?:html?|php)$/i, '/');
    const domain = currentUrl.hostname;

    let parentOrigin = HEATMAP_DEFAULT_ORIGIN;
    let parentHost = '';

    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;

    const panelId = queryStringMap['panel_id'];

    // Origin以外
    let iframeTokenOrigin = '';
    let iframeTokenVerification = Promise.resolve(false);
    const IFRAME_ALLOW_EXPECTED_TOKEN_HASH = '6e6f6dd001b6733d783b7f95cc9c21ccb2d5751e';

    // レンダリング時に渡すパラメータ
    const previewParameter = {
      requestId: '',
      dateStart: '',
      dateEnd: '',
      mode: '',
      showClickCounts: false,
      noiseSettings: {clickMinCount: 1, clickReferencePercentile: 95, clickRelativePercent: 0, attentionUpperPercent: 1, clickSensitivity: 100, attentionSensitivity: 100},
      attentionModeOptions: {second: false, arrival: false, arrivalTransition: false, leave: false},
      cvFilter: 'all',
      isPageDetectMode : 'off',
      adNetwork: 'all',
      agentId: 0,
      adId: 0,
    };
    let previewParameterVersion = 0;

    // データID選択モード
    let isDataIdSelectModeEnabled = false;

    // 要素別のデータ表示用
    let elementClickDatas = {};
    let elementAttentionDatas = {};

    const clickCountOverlay = createClickCountOverlay({dataIdKey: heatmapDataIdKey, isVisible: isVisibilityElement});
    const updateClickCountOverlay = () => {
      clickCountOverlay.update(elementClickDatas, previewParameter.mode === 'click' && previewParameter.showClickCounts && !isDataIdSelectModeEnabled);
    };

    let elHeatmapToolTip = null;

    // 滞在データの比較モード（true = Ptエンジン系データ内相対比較、false = 固定値比較）
    let isAttentionScaleRelative = true;

    // ヒートマップ用属性付与
    setHeatmapAttribute(document, {reconcile: true});
    let lpStateDataIds = await fetchLpStateDataIds(domain, currentPathname);
    let lpStateKey = pickLpStateKeyValue(lpStateDataIds);
    
    let heatmapDomChangeObserveElements = await getHeatmapDomChangeObserveElements();

    //=======================
    //DOMの変更を監視
    //=======================
    let attachDomChangeObserverTimerId = null;

    let structureDirty = true;
    let refreshRunning = false;
    let refreshAgain = false;
    let previewScrolling = false;
    let scrollStopTimerId = null;
    let refreshAfterScroll = false;
    let renderAfterScroll = false;
    const cancelIdleRefresh = () => {
      if (attachDomChangeObserverTimerId === null) return;
      const cancelIdle = window.cancelIdleCallback || clearTimeout;
      cancelIdle(attachDomChangeObserverTimerId);
      attachDomChangeObserverTimerId = null;
    };
    const onPreviewScroll = () => {
      previewScrolling = true;
      // Invalidate asynchronous draws, but keep their query available for reuse.
      previewParameterVersion++;
      renderAfterScroll = true;
      cancelIdleRefresh();
      clearTimeout(renderHeatmapDebounceTimerId);
      clearTimeout(scrollStopTimerId);
      scrollStopTimerId = setTimeout(() => {
        scrollStopTimerId = null;
        previewScrolling = false;
        if (refreshAfterScroll) attachDomChangeObserver();
        else if (renderAfterScroll) renderHeatmap();
      }, 1000);
    };
    const attachDomChangeObserver = async() => {
        refreshAfterScroll = true;
        if (previewScrolling) return;
        if (refreshRunning) { refreshAgain = true; return; }

        //一応ポリフィル化
        const runIdle = window.requestIdleCallback || ((fn, _) => setTimeout(fn, 100));
        cancelIdleRefresh();

        attachDomChangeObserverTimerId = runIdle(async() => {
          attachDomChangeObserverTimerId = null;
          if (previewScrolling) return;
          refreshAfterScroll = false;
          refreshRunning = true;
          const rebuildIds = structureDirty;
          structureDirty = false;
          domChangeObserver.disconnect();
          try {
            setHeatmapAttribute(document, {reconcile: true, reuseIds: !rebuildIds});
            domChangeObserver.observe(document.body, { childList: true, characterData: true, subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['style', 'class', 'src', 'srcset', 'poster', 'hidden', 'data-c-heatmap-content-key'] });
            [heatmapDomChangeObserveElements, lpStateDataIds] = await Promise.all([
              getHeatmapDomChangeObserveElements(), fetchLpStateDataIds(domain, currentPathname),
            ]);

            lpStateKey = pickLpStateKeyValue(lpStateDataIds);
          
            renderHeatmap();
          } finally {
            refreshRunning = false;
            if (refreshAgain) { refreshAgain = false; attachDomChangeObserver(); }
          }
        }, {timeout: 1000});
    }

    let domChangedTimerId = null;
    const domChangeObserver = new MutationObserver((mutations) => {
        let isChanged = false;
        const isOverlay = node => node.nodeType === 1 && (String(node.className || '').includes('heatmap-preview-') || String(node.className || '').includes('heatmap-canvas') || String(node.className || '').includes('heatmap-tooltip'));
        for (const m of mutations) {
          if (m.type === 'characterData') {
            if (m.target.parentElement?.closest?.('[class*="heatmap-preview-"],.heatmap-tooltip')) continue;
            isChanged = true;
          }
          if (m.type === 'childList') {
            // Ignore the renderer's own overlays and their descendants.
            if (m.target.closest?.('[class*="heatmap-preview-"],.heatmap-tooltip')) continue;
            if ([...m.addedNodes, ...m.removedNodes].some(n => !isOverlay(n))) { structureDirty = true; isChanged = true; }
          } else if (m.type === 'attributes') {
            if (isOverlay(m.target) || m.target.closest?.('[class*="heatmap-preview-"],.heatmap-tooltip')) continue;
            if (m.target === document.body && m.attributeName === 'style' && !/(display\s*:|opacity\s*:|visibility\s*:)/i.test(`${m.oldValue || ''};${m.target.getAttribute('style') || ''}`)) continue;
            if (m.attributeName === 'class' && !hasHeatmapContentClassChanged(m.oldValue, m.target.getAttribute('class'))) continue;
            isChanged = true;
          }
        }

        if(isChanged) {
          previewParameterVersion++;
          clearTimeout(domChangedTimerId);
          domChangedTimerId = setTimeout(() => attachDomChangeObserver(), DOM_CHANGE_DEBOUNCE_MS);
        }
    });

    {
      //※初期DOMが安定してから処理開始
      let initCompleteTimer = null;
      let domWatchStartTime = Date.now();
      let lastBodyHeight = getPageHeight();
      let lastHeightChangedAt = domWatchStartTime;

      const initCompleteObserver = new MutationObserver(() => {
          const h = getPageHeight();

          if(h != lastBodyHeight) {
              lastBodyHeight = h;
              lastHeightChangedAt = Date.now();
          }

          clearTimeout(initCompleteTimer);
          initCompleteTimer = setTimeout(async() => {
              const now = Date.now();
              const stableElapsed = now - lastHeightChangedAt;
              const totalElapsed  = now - domWatchStartTime;

              if(stableElapsed >= DOM_STABLE_DURATION || totalElapsed >= DOM_INIT_FORCE_STANDBY_MS) {
                  initCompleteObserver.disconnect();
                  clearTimeout(initCompleteTimer);
                  initCompleteTimer = null;

                  // ↓ DOMの監視を開始 ↓
                  await attachDomChangeObserver();
                  // ↑ DOMの監視を開始 ↓

                  if (document.readyState === 'complete') {
                      renderHeatmap();
                  } else {
                      window.addEventListener('load', function () {
                          renderHeatmap();
                      }, { once: true });
                  }
              }
          }, DOM_STABLE_DURATION);
      });

      heatmapDomChangeObserveElements.forEach((el) => {
          initCompleteObserver.observe(el, {
              attributes: true,
          });
      });

      //所定時間経ってもMutationObserverによる検知がない場合、安定とみなす。
      const forceInitTimer = setTimeout(async() => {
          clearTimeout(forceInitTimer);
          if(initCompleteTimer === null && initCompleteObserver.takeRecords().length === 0) {
              initCompleteObserver.disconnect();

              // ↓ DOMの監視を開始 ↓
              await attachDomChangeObserver();
              // ↑ DOMの監視を開始 ↓

              if (document.readyState === 'complete') {
                  renderHeatmap();
              } else {
                  window.addEventListener('load', function() {
                      renderHeatmap();
                  }, {once: true});
              }
          }
      }, DOM_INIT_FORCE_STANDBY_MS);
    }

    const io = new IntersectionObserver(entries => {
      if (entries.some(en => en.isIntersecting)) attachDomChangeObserver();
    }, { threshold: 0 });

    for(const el of heatmapDomChangeObserveElements) {
      io.observe(el);
    }
    
    //===================================
    //高さが変化した場合はiframeに通知して再レンダリング↓↓↓
    //===================================
    const resizeBody = () => {
        previewParameterVersion++;

        requestAnimationFrame(() => {
            renderHeatmap();
        });
    }
    
    let lastPageHeight = getPageHeight();

    const heatmapElementResizeObserver = new ResizeObserver(e => {
        const currentPageHeight = getPageHeight();
        
        //マーケティングタグなどでのリサイズは回避
        if(lastPageHeight - currentPageHeight > 2 || currentPageHeight - lastPageHeight > 2) {
          lastPageHeight = currentPageHeight;
          resizeBody();
        }
    }); 
    heatmapElementResizeObserver.observe(document.body);


    // body以上にcanvasが伸びるときがあるときの対策
    setInterval(() => {
      const canvas = document.querySelector('.heatmap-canvas');
      if(canvas) {
        const bh = Number(getComputedStyle(document.body).height.replace('px', ''));
        const ch = Number(getComputedStyle(document.querySelector('.heatmap-canvas')).height.replace('px', ''));
        if(bh < ch) {
          requestAnimationFrame(() => {
            renderHeatmap();
          });
        }
      }
    }, 2000);
    //===================================
    //高さが変化した場合はiframeに通知して再レンダリング↑↑↑
    //===================================

    //===========================
    // ヒートマップ描画
    //===========================
    let renderHeatmapDebounceTimerId = null;
    let renderFetchAbort = null;
    // Cache the exact current query. Explicit parameters always refresh the data.
    let previewDataCache = null;
    const invalidatePreviewData = () => {
      renderFetchAbort?.abort();
      previewDataCache = null;
    };
    const loadPreviewData = (postData) => {
      const key = JSON.stringify([parentOrigin, {...postData, dataIds: postData.dataIds?.slice().sort()}]);
      if (previewDataCache?.key === key) return previewDataCache.promise;
      renderFetchAbort?.abort();
      const controller = renderFetchAbort = new AbortController();
      const promise = fetch(`${parentOrigin}${HEATMAP_PREVIEW_WS}`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(postData), signal: controller.signal,
      }).then(res => {
        if (res.ok === false) throw new Error(`Preview request failed: ${res.status}`);
        return res.json();
      }).catch(error => {
        if (previewDataCache?.promise === promise) previewDataCache = null;
        throw error;
      });
      previewDataCache = {key, promise};
      return promise;
    };
    let canvasMode = '';
    let parentViewport = null;
    const canvases = new Map();
    const previewViewport = () => parentViewport || {
      top: window.scrollY, bottom: window.scrollY + windowHeight,
    };
    const previewCanvas = (slot, engine, config, mode) => {
      if (canvasMode !== mode) {
        for (const entry of canvases.values()) entry.destroy();
        canvases.clear();
        canvasMode = mode;
      }
      const style = getComputedStyle(config.container);
      const width = config.width ? windowWidth : parseFloat(style.width);
      const height = parseFloat(style.height);
      let entry = canvases.get(slot);
      if (!entry) {
        entry = createPreviewTileLayer(engine, config, () => {
          const viewport = previewViewport();
          const offset = slot === 'fixed' ? window.scrollY : 0;
          return {top: viewport.top - offset, bottom: viewport.bottom - offset};
        });
        canvases.set(slot, entry);
      }
      entry.resize(width, height);
      return entry;
    };
    const updateParentViewport = value => {
      if (value === null) { parentViewport = null; return; }
      if (!value || !Number.isFinite(value.top) || !Number.isFinite(value.bottom) || value.bottom < value.top) return;
      parentViewport = {top: Math.max(0, value.top), bottom: Math.max(0, value.bottom)};
    };

    // iframeの高さだけが変わり、LP全体の高さが変わらない場合も座標を取り直す。
    window.addEventListener('resize', () => {
      windowWidth = window.innerWidth;
      windowHeight = window.innerHeight;
      resizeBody();
    }, {passive: true});
    // Capture scrolls in nested LP containers as well as the document viewport.
    // Click-count badges retain their own lightweight scroll handler.
    window.addEventListener('scroll', onPreviewScroll, {capture: true, passive: true});

    async function renderHeatmap () {
        try {
            renderAfterScroll = true;
            clearTimeout(renderHeatmapDebounceTimerId);
            if (previewScrolling) return;
            renderHeatmapDebounceTimerId = setTimeout(async() => {
                renderHeatmapDebounceTimerId = null;
                if (previewScrolling) return;
                renderAfterScroll = false;
                const parameterVersion = ++previewParameterVersion;
                const renderParameter = {...previewParameter, attentionModeOptions: {...previewParameter.attentionModeOptions}, noiseSettings: {...previewParameter.noiseSettings}};
                // 画像の読み込み失敗や遅延でデータ取得を止めない。
                // 画像の load と要素のリサイズ時には、確定した位置で再描画する。

                if(renderParameter.dateStart === '') return;
                if(renderParameter.dateEnd === '') return;
                if(renderParameter.mode === '') return;

                const pageHeight = getPageHeight();

                if (document.body.style.height !== 'initial') document.body.style.height = 'initial';

                // データIDリスト取得
                const existDataIdsElementMap = new Map();
                document.querySelectorAll(`[${heatmapDataIdKey}]`).forEach(el => {
                  const id = el.getAttribute(heatmapDataIdKey);
                  if (id) existDataIdsElementMap.set(id, el);
                  if (renderParameter.mode === 'attention') {
                    const attentionId = getAttentionDataId(el);
                    if (attentionId) existDataIdsElementMap.set(attentionId, el);
                  }
                });

                let postData = {};
                postData.domain = domain;
                postData.uri = currentPathname;
                postData.lpStateKey = lpStateKey;
                postData.isPageDetectMode = renderParameter.isPageDetectMode;
                postData.mode = renderParameter.mode;
                postData.cvFilter = renderParameter.cvFilter;
                postData.dateStart = renderParameter.dateStart;
                postData.dateEnd = renderParameter.dateEnd;
                postData.noiseSettings = renderParameter.noiseSettings;
                if(renderParameter.adNetwork && renderParameter.adNetwork !== 'all') postData.adNetwork = renderParameter.adNetwork;
                if(renderParameter.agentId > 0) postData.agentId = renderParameter.agentId;
                if(renderParameter.adId > 0) postData.adId = renderParameter.adId;
                if(existDataIdsElementMap.size > 0) postData.dataIds = [...existDataIdsElementMap.keys()];

                //===========================
                //クリック
                //===========================
                if(renderParameter.mode === 'click') {
                    try {
                        const pendingData = loadPreviewData(postData);
                        const renderController = renderFetchAbort;
                        const rawJson = await pendingData;
                        if (renderController.signal.aborted || parameterVersion !== previewParameterVersion) return;
                        const data = rawJson.data || rawJson;

                        elementClickDatas = data.clickDataIds ?? {};
                        updateClickCountOverlay();

                        const radius = 15;
                        const heatmapInstance = previewCanvas('page', h337, {
                            container: document.body,
                            radius: radius,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            gradient: {
                                0.0: '#0000ff',  // 青
                                0.2: '#00ffff',  // 水色
                                0.4: '#00ff00',  // 緑
                                0.6: '#ffff00',  // 黄
                                0.8: '#ff8000',  // オレンジ
                                1.0: '#ff0000'   // 赤
                            }
                        }, renderParameter.mode);

                        const dataPoints = data.dataPoints;
                        const logMax = Math.log((data.max ?? 0) + 1);
                        let maxVal = 0;

                        const heatmapDataPoints = [];

                        const heatmapFixedElements = [];
                        const heatmapFixedDataPoints = [];

                        for(const dataId of Object.keys(dataPoints)) {
                            const el = existDataIdsElementMap.get(dataId);

                            if(!dataPoints[dataId]) continue;
                            if(!el) continue;
                            if(!el.classList.contains(heatmapClickClass)) continue;

                            const dset = dataPoints[dataId];
                            const rect = el.getBoundingClientRect();
                            
                            if(!isVisibilityElement(el)) continue;
                            if(isFixed(el)) {
                              heatmapFixedElements.push(el);
                              continue;
                            }
                            
                            Object.keys(dset).forEach((k) => {
                                const d = dset[k];
                                const value = logMax > 0 ? Math.log(d.value + 1) / logMax * 1000 : 0;

                                if (value > maxVal) maxVal = value;

                                heatmapDataPoints.push({
                                    x: Number((rect.left + d.x * rect.width).toFixed(0)),
                                    y: Number(((window.scrollY + rect.top) + d.y * rect.height).toFixed(0)),
                                    value: value,
                                });
                            });
                        }

                        // Fixed要素専用
                        const fixedContainer = await (async() => {
                            const c = document.querySelector(`.heatmap-preview-fixed-container`);
                            if(c !== null) return c;

                            document.querySelector(`body`).insertAdjacentHTML('beforeend', `<div class="heatmap-preview-fixed-container"></div>`);

                            return await new Promise((resolve) => {
                                requestAnimationFrame(() => {
                                    const a = document.querySelector(`.heatmap-preview-fixed-container`);
                                    resolve(a);
                                });
                            });
                        })();

                        if (renderController.signal.aborted || parameterVersion !== previewParameterVersion) return;
                        if(fixedContainer !== null) {
                            const heatmapFixedInstance = previewCanvas('fixed', h337, {
                                  container: fixedContainer,
                                  radius: radius,
                                  backgroundColor: 'rgba(0, 0, 0, 0)',
                                  gradient: {
                                      0.0: '#0000ff',  // 青
                                      0.2: '#00ffff',  // 水色
                                      0.4: '#00ff00',  // 緑
                                      0.6: '#ffff00',  // 黄
                                      0.8: '#ff8000',  // オレンジ
                                      1.0: '#ff0000'   // 赤
                                  }
                            }, renderParameter.mode);

                            for(const el of heatmapFixedElements) {
                                if(!dataPoints[el.getAttribute(heatmapDataIdKey)]) continue;

                                const dset = dataPoints[el.getAttribute(heatmapDataIdKey)];
                                const rect = el.getBoundingClientRect();
                                
                                if(!isVisibilityElement(el)) continue;
                                if(!isFixed(el)) continue;
                                
                                Object.keys(dset).forEach((k) => {
                                    const d = dset[k];
                                    const value = logMax > 0 ? Math.log(d.value + 1) / logMax * 1000 : 0;

                                    if (value > maxVal) maxVal = value;

                                    heatmapFixedDataPoints.push({
                                        x: Number((rect.left + d.x * rect.width).toFixed(0)),
                                        y: Number((rect.top + d.y * rect.height).toFixed(0)),
                                        value: value,
                                    });
                                });
                            }

                            heatmapFixedInstance.setData({
                                max: Math.max(maxVal, 1) * 100 / renderParameter.noiseSettings.clickSensitivity,
                                data: heatmapFixedDataPoints
                            });
                        }

                        // 通常要素と固定要素に同じ色の基準を適用し、クリック自体は残す。
                        heatmapInstance.setData({
                            max: Math.max(maxVal, 1) * 100 / renderParameter.noiseSettings.clickSensitivity,
                            data: heatmapDataPoints
                        });

                        // 親フレームへ
                        window.parent.postMessage({
                          panelId: panelId,
                          type: 'renderComplete',
                          requestId: renderParameter.requestId,
                          mode: 'click', 
                          pageHeight: pageHeight,
                          lpStateKey: lpStateKey,
                          pvTotal: data.pvTotal,
                          pvState: data.pvState,
                          clickTotal: data.clickTotal,
                          clickState: data.clickState,
                          cvTotal: data.cvTotal,
                          cvState: data.cvState,
                        }, parentOrigin); 

                    } catch (e) {
                      if(e?.name === 'AbortError') return;
                      console.error(e);
                    }
                }

                //===========================
                //滞在
                //===========================
                if(renderParameter.mode === 'attention') {
                    try {
                        const pendingData = loadPreviewData(postData);
                        const renderController = renderFetchAbort;
                        const rawJson = await pendingData;
                        if (renderController.signal.aborted || parameterVersion !== previewParameterVersion) return;
                        const data = rawJson.data || rawJson;

                        const canvasWidth = 240;

                        let ch = HEATMAP_PREVIEW_HASH_HEIGHT + Math.floor(HEATMAP_PREVIEW_HASH_HEIGHT / 4); //収集ロジックとぴっちりだとつなぎ目で抜けが出やすいのでちょっと広げて滑らかに
                        if(ch % 2 === 0) ch += 1; //奇数化

                        const heatmapInstance = previewCanvas('page', h337square, {
                            container: document.body,
                            radius: 0,
                            width: canvasWidth,
                            c_width: canvasWidth,
                            c_height: ch,
                            opacity: 0.65,
                            blur: 0.85,
                            gradient: {
                                0.0: '#0000ff',  // 青
                                0.2: '#00ffff',  // 水色
                                0.4: '#00ff00',  // 緑
                                0.6: '#ffff00',  // 黄
                                0.8: '#ff8000',  // オレンジ
                                1.0: '#ff0000'   // 赤
                            }
                        }, renderParameter.mode);

                        const dataPoints = data.dataPoints;
                        const heatmapDataPoints = [];
                        const DATA_POINT_Y_ROUND = 20;
                        const MAX_CANVAS = 1000;

                        // デフォルトは相対ベース。比較用に固定ベースも用意。
                        const attentionRelativeMax = Math.max(Number(data.attentionReferenceValue ?? data.max ?? 0) || 0, 1);
                        const attentionFixedMax = 600; //1分

                        const maxAttention = isAttentionScaleRelative ? attentionRelativeMax : attentionFixedMax;

                        const attentionGamma = isAttentionScaleRelative ? 0.95 : 1;

                        for(const dataId of Object.keys(dataPoints)) {
                            const el = existDataIdsElementMap.get(dataId);
                            if(!el) continue;
                            if(!el.classList.contains(heatmapAttentionClass)) continue;

                            const visible = getAttentionVisibleRect(el, false);
                            if(visible) {
                                const dset = dataPoints[dataId];
                                const rect = el.getBoundingClientRect();

                                for (const d of Object.values(dset)) {
                                    const pointY = rect.top + Number(d.y) * rect.height;
                                    if (pointY < visible.top || pointY > visible.bottom) continue;
                                    const rawValue = Number(d.value) || 0;
                                    const rawSecond = rawValue;
                                    const ratio = maxAttention > 0 ? Math.min(1, rawSecond / maxAttention) : 0;
                                    const useValue = Math.pow(ratio, attentionGamma) * MAX_CANVAS;

                                    heatmapDataPoints.push({
                                      x: Math.floor(canvasWidth / 2),
                                      y: roundCalc(Math.floor(window.scrollY + rect.top + d.y * rect.height), DATA_POINT_Y_ROUND),
                                      value: Math.floor(useValue),
                                    });
                                }
                            }
                        }

                        const mergeHeatmapDataPoints = {};
                        for(const point of heatmapDataPoints) {
                            const cursor = mergeHeatmapDataPoints[point.y];

                            if(!cursor) {
                                mergeHeatmapDataPoints[point.y] = point;
                                continue;
                            }

                            if(cursor.value < point.value) {
                                mergeHeatmapDataPoints[point.y] = point;
                            }
                        }

                        // The server reference includes the entire selected dataset.
                        // Re-normalizing drawable points would change colours on lazy load.
                        const setData = Object.values(mergeHeatmapDataPoints).map(d => ({ ...d, value: Math.round(d.value) }));

                        heatmapInstance.setData({
                            min: 0,
                            max: 1000 * 100 / renderParameter.noiseSettings.attentionSensitivity,
                            data: setData,
                        });

                        // 親フレームへ
                        window.parent.postMessage({
                          panelId: panelId,
                          type: 'renderComplete',
                          requestId: renderParameter.requestId,
                          mode: 'attention',
                          attentionCalculation: data.attentionCalculation,
                          pageHeight: pageHeight,
                          lpStateKey: lpStateKey,
                          pvTotal: data.pvTotal,
                          pvState: data.pvState,
                          clickTotal: data.clickTotal,
                          clickState: data.clickState,
                          cvTotal: data.cvTotal,
                          cvState: data.cvState,
                        }, parentOrigin);

                        // ==============
                        // 滞在秒数
                        // ==============
                        if(data.attentionSeconds && renderParameter.attentionModeOptions.second) {
                            const segmentRange = 200;
                            const segmentRound = 25;

                            const fetchSecondData = data.attentionSeconds;
                            const rectSecondData = {};
                            for(let i = 0; i * segmentRange <= pageHeight + segmentRange; i++) {
                                rectSecondData[i * segmentRange] = 0;
                            }

                            for(const dataId of Object.keys(dataPoints)) {
                                const el = existDataIdsElementMap.get(dataId);

                                if(!el) continue;
                                const visible = getAttentionVisibleRect(el, false);
                                if(!visible) continue;
                                if(!el.classList.contains(heatmapAttentionClass)) continue;
                                if(!fetchSecondData[dataId]) continue;

                                const raitoSeconds = fetchSecondData[dataId];
                                const rect = el.getBoundingClientRect();
                                for(const [raito, val] of Object.entries(raitoSeconds)) {
                                    const pointY = rect.top + Number(raito) * rect.height;
                                    if (pointY < visible.top || pointY > visible.bottom) continue;
                                    let elTop = roundCalc((window.scrollY + rect.top + Number(raito) * rect.height), segmentRound);

                                    if (elTop > pageHeight) {
                                        elTop = Math.floor(pageHeight / segmentRound) * segmentRound;
                                    }

                                    const v = Number(val);
                                    if (rectSecondData[elTop] === undefined) {
                                        rectSecondData[elTop] = 0;
                                    }

                                    // 滞在時間は最大値を取る
                                    if (rectSecondData[elTop] < v) {
                                        rectSecondData[elTop] = v;
                                    }
                                }
                            }

                            const mergeRectSecondData = {};
                            for(const [y, val] of Object.entries(rectSecondData)) {

                                const newY = roundCalc(Number(y), segmentRange);
                                if(!mergeRectSecondData[newY]) {
                                    mergeRectSecondData[newY] = 0;
                                }

                                if(mergeRectSecondData[newY] < val) {
                                    mergeRectSecondData[newY] = val;
                                }
                            }

                            const attentionSecondBarContainer = document.querySelector('.heatmap-preview-attention-second-container');
                            const attentionSecondBarHtmls = [];

                            for(const [y, val] of Object.entries(mergeRectSecondData)) {
                              let renderY = Number(y);
                              if (renderY > pageHeight - 30) renderY = pageHeight - 30; // はみ出しをクリップ

                              attentionSecondBarHtmls.push(
                                `<p class="heatmap-preview-attention-second" style="top: ${renderY}px;">
                                    <span class="heatmap-preview-attention-second-value">
                                      ${
                                      0 <= val ? `${val}秒` : `0秒未満`
                                      }
                                    </span>
                                </p>`
                              );
                            }

                            attentionSecondBarContainer.innerHTML = attentionSecondBarHtmls.join('');
                        } else {
                          const attentionSecondBarContainer = document.querySelector('.heatmap-preview-attention-second-container');
                          if(attentionSecondBarContainer) attentionSecondBarContainer.innerHTML = '';
                        }

                        // ==============
                        // 読了率
                        // ==============
                        if(data.arrivals && renderParameter.attentionModeOptions.arrival) {
                            const segmentRange = 200;
                            const segmentRound = 25;
                            const fetchArrivalData = data.arrivals;
                            const rectArrivalData = {};
                            for(let i = 0; i * segmentRange <= pageHeight + segmentRange; i++) {
                                rectArrivalData[i * segmentRange] = 0;
                            }

                            for (const dataId of Object.keys(fetchArrivalData)) {
                                const el = existDataIdsElementMap.get(dataId);

                                if(!el) continue;
                                if(!isAttentionCandidateElement(el)) continue;

                                const arrival = fetchArrivalData[dataId];
                                const rect = el.getBoundingClientRect();
                                for(const [raito, val] of Object.entries(arrival)) {
                                    let elTop = Math.floor((window.scrollY + rect.top + Number(raito) * rect.height) / segmentRound) * segmentRound;

                                    if (elTop > pageHeight) {
                                        elTop = Math.floor(pageHeight / segmentRound) * segmentRound;
                                    }

                                    if (rectArrivalData[elTop] === undefined) rectArrivalData[elTop] = 0;
                                    // Math.max を廃止し、重複しない要素ごとの絶対値として扱うため += で合算
                                    rectArrivalData[elTop] = Math.max(rectArrivalData[elTop], Number(val));
                                }
                            }

                            const mergeRectArrivalData = {};
                            for(const[y, val] of Object.entries(rectArrivalData)) {
                                const newY = roundCalc(Number(y), segmentRange);
                                if(mergeRectArrivalData[newY] === undefined) {
                                    mergeRectArrivalData[newY] = 0;
                                }
                                // 重複爆発を防ぐため、同じY帯の最高到達者数を採用する
                                mergeRectArrivalData[newY] = Math.max(mergeRectArrivalData[newY], val);
                            }

                            const sortedArrivalData = Object.entries(mergeRectArrivalData)
                              .filter(([y]) => Number.isFinite(Number(y)))
                              .map(([y, v]) => [Number(y), Number(v) || 0])
                              .sort((a, b) => a[0] - b[0]);

                            // 反単調イソトニック回帰（PAVA）で最小改変の単調“非増加”化
                            const antitonicPAVA = (pairs) => {
                                // pairs: [[y, v], ...]（y 昇順）
                                const blocks = [];
                                for (const [y, v] of pairs) {
                                    blocks.push({ ys: [y], sum: v, w: 1, avg: v });
                                    while (blocks.length >= 2 && blocks[blocks.length - 2].avg < blocks[blocks.length - 1].avg) {
                                        const b = blocks.pop();
                                        const a = blocks.pop();
                                        const merged = { ys: a.ys.concat(b.ys), sum: a.sum + b.sum, w: a.w + b.w };
                                        merged.avg = merged.sum / merged.w;
                                        blocks.push(merged);
                                    }
                                }
                                const out = [];
                                for (const blk of blocks) for (const y of blk.ys) out.push([y, blk.avg]);
                                out.sort((a, b) => a[0] - b[0]);
                                return out;
                            }

                            const monoArrival = antitonicPAVA(sortedArrivalData);
                            const topBase = monoArrival.length ? monoArrival[0][1] : 0; // 先頭が最大

                            const arrivalPerData = {};
                            if (topBase > 0) {
                                monoArrival.forEach(([y, v]) => {
                                    if (y <= pageHeight && y >= 0) {
                                        const per = (v / topBase) * 100;
                                        arrivalPerData[y] = Math.min(100, Number(per.toFixed(1)));
                                    }
                                });
                            } 

                            const arrivalBarContainer = document.querySelector('.heatmap-preview-arrival-container');
                            const arrivalBarHtmls = [];

                            for(const [y, val] of Object.entries(arrivalPerData)) {
                                let per = Number(val).toFixed(1);
                                let renderY = Number(y);
                                if (renderY > pageHeight - 30) renderY = pageHeight - 30; // はみ出しをクリップ

                                arrivalBarHtmls.push(
                                  `<p class="heatmap-preview-arrival" style="top: ${renderY}px;">
                                      <span class="heatmap-preview-arrival-per">${per}%</span>
                                  </p>`
                                );
                            }

                            arrivalBarContainer.innerHTML = arrivalBarHtmls.join('');
                        } else {
                          const arrivalBarContainer = document.querySelector('.heatmap-preview-arrival-container');
                          if(arrivalBarContainer) arrivalBarContainer.innerHTML = '';
                        }

                        // ==============
                        // 読了率（遷移率考慮）
                        // ==============
                        if(data.arrivals && renderParameter.attentionModeOptions.arrivalTransition) {
                            const segmentRange = 200;
                            const segmentRound = 25;
                            const fetchArrivalData = data.arrivals;
                            const rectArrivalData = {};
                            for(let i = 0; i * segmentRange <= pageHeight + segmentRange; i++) {
                                rectArrivalData[i * segmentRange] = 0;
                            }

                            for (const dataId of Object.keys(fetchArrivalData)) {
                                const el = existDataIdsElementMap.get(dataId);

                                if(!el) continue;
                                if(!isAttentionCandidateElement(el)) continue;

                                const arrival = fetchArrivalData[dataId];
                                const rect = el.getBoundingClientRect();
                                for(const [raito, val] of Object.entries(arrival)) {
                                    let elTop = Math.floor((window.scrollY + rect.top + Number(raito) * rect.height) / segmentRound) * segmentRound;

                                    if (elTop > pageHeight) {
                                        elTop = Math.floor(pageHeight / segmentRound) * segmentRound;
                                    }

                                    if (rectArrivalData[elTop] === undefined) rectArrivalData[elTop] = 0;
                                    rectArrivalData[elTop] = Math.max(rectArrivalData[elTop], Number(val));
                                }
                            }

                            const mergeRectArrivalData = {};
                            for(const[y, val] of Object.entries(rectArrivalData)) {
                                const newY = roundCalc(Number(y), segmentRange);
                                if(mergeRectArrivalData[newY] === undefined) {
                                    mergeRectArrivalData[newY] = 0;
                                }
                                mergeRectArrivalData[newY] = Math.max(mergeRectArrivalData[newY], val);
                            }

                            const sortedArrivalData = Object.entries(mergeRectArrivalData)
                              .filter(([y]) => Number.isFinite(Number(y)))
                              .map(([y, v]) => [Number(y), Number(v) || 0])
                              .sort((a, b) => a[0] - b[0]);

                            // 反単調イソトニック回帰（PAVA）で最小改変の単調“非増加”化
                            const antitonicPAVA = (pairs) => {
                                // pairs: [[y, v], ...]（y 昇順）
                                const blocks = [];
                                for (const [y, v] of pairs) {
                                    blocks.push({ ys: [y], sum: v, w: 1, avg: v });
                                    while (blocks.length >= 2 && blocks[blocks.length - 2].avg < blocks[blocks.length - 1].avg) {
                                        const b = blocks.pop();
                                        const a = blocks.pop();
                                        const merged = { ys: a.ys.concat(b.ys), sum: a.sum + b.sum, w: a.w + b.w };
                                        merged.avg = merged.sum / merged.w;
                                        blocks.push(merged);
                                    }
                                }
                                const out = [];
                                for (const blk of blocks) for (const y of blk.ys) out.push([y, blk.avg]);
                                out.sort((a, b) => a[0] - b[0]);
                                return out;
                            }

                            const monoArrival = antitonicPAVA(sortedArrivalData);
                            const topBase = monoArrival.length ? monoArrival[0][1] : 0; // 先頭が最大

                            const arrivalPerData = {};
                            if (topBase > 0) {
                                monoArrival.forEach(([y, v]) => {
                                    if (y <= pageHeight && y >= 0) {
                                        const per = (v / topBase) * 100;
                                        arrivalPerData[y] = Math.min(100, Number(per.toFixed(1)));
                                    }
                                });
                            } 

                            const arrivalBarContainer = document.querySelector('.heatmap-preview-arrival-transition-container');
                            const arrivalBarHtmls = [];

                            const pv = Number(data.pvTotal ?? 0);
                            const pvState = Number(data.pvState ?? 0);
                            // pvが0の場合のパーセント大爆発を防ぐため、1で代用する
                            const transitionPer = pv > 0 ? (pvState / pv) : 1; 

                            for(const [y, val] of Object.entries(arrivalPerData)) {
                                const per = (Number(val) * transitionPer).toFixed(1);
                                let renderY = Number(y);
                                if (renderY > pageHeight - 30) renderY = pageHeight - 30; // はみ出しをクリップ

                                arrivalBarHtmls.push(
                                  `<p class="heatmap-preview-arrival-transition" style="top: ${renderY}px;">
                                      <span class="heatmap-preview-arrival-transition-per">${per}%</span>
                                  </p>`
                                );
                            }

                            arrivalBarContainer.innerHTML = arrivalBarHtmls.join('');
                        } else {
                          const arrivalBarContainer = document.querySelector('.heatmap-preview-arrival-transition-container');
                          if(arrivalBarContainer) arrivalBarContainer.innerHTML = '';
                        }

                        // ==============
                        // 離脱率
                        // ==============
                        if(data.arrivals && renderParameter.attentionModeOptions.leave) {
                            const segmentRange = 200;
                            const segmentRound = 25;
                            const fetchArrivalData = data.arrivals;
                            const rectArrivalData = {};
                            
                            for(let i = 0; i * segmentRange <= pageHeight + segmentRange; i++) {
                                rectArrivalData[i * segmentRange] = 0;
                            }

                            for (const dataId of Object.keys(fetchArrivalData)) {
                                const el = existDataIdsElementMap.get(dataId);
                                if(!el) continue;
                                if(!isAttentionCandidateElement(el)) continue;

                                const arrival = fetchArrivalData[dataId];
                                const rect = el.getBoundingClientRect();
                                for(const [raito, val] of Object.entries(arrival)) {
                                    let elTop = Math.floor((window.scrollY + rect.top + Number(raito) * rect.height) / segmentRound) * segmentRound;
                                    if (elTop > pageHeight) {
                                        elTop = Math.floor(pageHeight / segmentRound) * segmentRound;
                                    }

                                    if (rectArrivalData[elTop] === undefined) rectArrivalData[elTop] = 0;
                                    rectArrivalData[elTop] = Math.max(rectArrivalData[elTop], Number(val));
                                }
                            }

                            const mergeRectArrivalData = {};
                            for(const [y, val] of Object.entries(rectArrivalData)) {
                                const newY = roundCalc(Number(y), segmentRange);
                                if(mergeRectArrivalData[newY] === undefined) {
                                    mergeRectArrivalData[newY] = 0;
                                }
                                mergeRectArrivalData[newY] = Math.max(mergeRectArrivalData[newY], Number(val));
                            }

                            const sortedArrivalData = Object.entries(mergeRectArrivalData)
                              .filter(([y]) => Number.isFinite(Number(y)))
                              .map(([y, v]) => [Number(y), Number(v) || 0])
                              .sort((a, b) => a[0] - b[0]);

                            const antitonicPAVA = (pairs) => {
                                const blocks = [];
                                for (const [y, v] of pairs) {
                                    blocks.push({ ys: [y], sum: v, w: 1, avg: v });
                                    while (blocks.length >= 2 && blocks[blocks.length - 2].avg < blocks[blocks.length - 1].avg) {
                                        const b = blocks.pop();
                                        const a = blocks.pop();
                                        const merged = { ys: a.ys.concat(b.ys), sum: a.sum + b.sum, w: a.w + b.w };
                                        merged.avg = merged.sum / merged.w;
                                        blocks.push(merged);
                                    }
                                }
                                const out = [];
                                for (const blk of blocks) for (const y of blk.ys) out.push([y, blk.avg]);
                                out.sort((a, b) => a[0] - b[0]);
                                return out;
                            }

                            let rawMonoArrival = antitonicPAVA(sortedArrivalData);
                            const pvState = Number(data.pvState ?? 0);
                            const monoArrival = [];

                            if (rawMonoArrival.length > 0) {
                                if (rawMonoArrival[0][0] > 0 && pvState > rawMonoArrival[0][1]) {
                                    monoArrival.push([0, pvState]);
                                } else if (rawMonoArrival[0][0] === 0 && pvState > rawMonoArrival[0][1]) {
                                    rawMonoArrival[0][1] = pvState;
                                }
                            }
                            monoArrival.push(...rawMonoArrival);

                            const leaveBarContainer = document.querySelector('.heatmap-preview-leave-container');
                            const leaveBarHtmls = [];

                            if (monoArrival.length > 0) {
                                for (let i = 0; i < monoArrival.length - 1; i++) {
                                    const currentY = monoArrival[i][0];
                                    const nextY = monoArrival[i + 1][0];
                                    const currentArrival = monoArrival[i][1];
                                    const nextArrival = monoArrival[i + 1][1];

                                    let dropOff = currentArrival - nextArrival;

                                    if (currentY <= pageHeight && currentY >= 0) {
                                        const per = (dropOff / (currentArrival > 0 ? currentArrival : 1)) * 100;
                                        let renderY = nextY;
                                        if (renderY > pageHeight - 30) renderY = pageHeight - 30;

                                        leaveBarHtmls.push(
                                            `<p class="heatmap-preview-leave" style="top: ${renderY}px;">
                                                <span class="heatmap-preview-leave-per">${per.toFixed(1)}%</span>
                                            </p>`
                                        );
                                    }
                                }
                            }

                            leaveBarContainer.innerHTML = leaveBarHtmls.join('');
                        } else {
                          const leaveBarContainer = document.querySelector('.heatmap-preview-leave-container');
                          if(leaveBarContainer) leaveBarContainer.innerHTML = '';
                        }

                    } catch (e) {
                      if (e?.name === 'AbortError') return;
                      console.error(e);
                    }   
                }
            }, HEATMAP_RENDER_DEBOUNCE_MS);
        } catch (e) {
          if (e?.name === 'AbortError') return;
          console.error(e);
        }
    }

    let loadResourceTimerId = null;
    const previewResourceChanged = e => {
      const target = e.target;
      if (!target || (target.tagName !== 'IMG' && target.tagName !== 'VIDEO')) return;
      clearTimeout(loadResourceTimerId);
      loadResourceTimerId = setTimeout(() => attachDomChangeObserver(), 100);
    };
    for (const event of ['load', 'loadeddata', 'loadedmetadata', 'emptied', 'error']) document.addEventListener(event, previewResourceChanged, true);

    /**
     * ポストメッセージリスナー
     */
    window.addEventListener('message', async(e) => {
      if (!e.data || e.source !== window.parent) return;
      if (e.data.type === 'allowToken') {
        if (!e.data.token) return;
        iframeTokenOrigin = e.origin;
        iframeTokenVerification = sha1(e.data.token)
          .then(hash => hash === IFRAME_ALLOW_EXPECTED_TOKEN_HASH)
          .catch(() => false);
      }

      const isAllowedOrigin = ALLOWED_ORIGINS.includes(e.origin);
      // READY直後に届く描画条件は、先行する認証の完了を待って処理する。
      if (!isAllowedOrigin && !(e.origin === iframeTokenOrigin && await iframeTokenVerification)) return;

      if(e.origin) parentOrigin = e.origin;
      parentHost = new URL(parentOrigin).hostname;

      // Full-page previews scroll in the parent rather than inside the iframe.
      if (e.data.type === 'previewViewport') {
          updateParentViewport(e.data.viewport);
          renderHeatmap();
          return;
      }
      if (e.data.type === 'previewScroll') {
          if ('viewport' in e.data) updateParentViewport(e.data.viewport);
          onPreviewScroll();
          return;
      }

      if(e.data.type === 'previewPending') {
          previewParameterVersion++;
          invalidatePreviewData();
          clearTimeout(renderHeatmapDebounceTimerId);
          previewParameter.mode = '';
          elementClickDatas = {};
          updateClickCountOverlay();
          document.querySelector('.heatmap-tooltip')?.classList.remove('is-show');
          return;
      }

      if(e.data.type === 'parameter') {
          previewParameterVersion++;
          invalidatePreviewData();
          // Clear counts immediately while new date/filter/mode data is loading.
          elementClickDatas = {};
          previewParameter.requestId = e.data.requestId ?? '';
          previewParameter.dateStart = e.data.dateStart ?? '';
          previewParameter.dateEnd = e.data.dateEnd ?? '';
          previewParameter.mode = e.data.mode ?? '';
          const noiseNumber = (value, fallback, maximum) => {
            if (value === undefined || value === null || value === '') return fallback;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? Math.max(0, Math.min(maximum, parsed)) : fallback;
          };
          const noise = e.data.noiseSettings ?? {};
          previewParameter.noiseSettings = {
            clickMinCount: Math.round(noiseNumber(noise.clickMinCount, 1, 1000000)),
            clickReferencePercentile: noiseNumber(noise.clickReferencePercentile, 95, 100),
            clickRelativePercent: noiseNumber(noise.clickRelativePercent, 0, 100),
            attentionUpperPercent: noiseNumber(noise.attentionUpperPercent, 1, 100),
            clickSensitivity: Math.max(25, noiseNumber(noise.clickSensitivity, 100, 200)),
            attentionSensitivity: Math.max(25, noiseNumber(noise.attentionSensitivity, 100, 200)),
          };
          previewParameter.attentionModeOptions = e.data.attentionModeOptions ?? {second: false, arrival: false, arrivalTransition: false, leave: false};
          previewParameter.cvFilter = e.data.cvFilter || 'all';
          previewParameter.isPageDetectMode = e.data.isPageDetectMode ?? '';
          previewParameter.adNetwork = e.data.adNetwork || 'all';
          previewParameter.agentId = e.data.agentId ?? 0;
          previewParameter.adId = e.data.adId ?? 0;
          updateClickCountOverlay();
          document.querySelector('.heatmap-tooltip')?.classList.remove('is-show');

          // 不要なviewの削除
          if(!previewParameter.attentionModeOptions.second) {
            const container = document.querySelector('.heatmap-preview-attention-second-container');
            if(container) container.innerHTML = '';  
          }

          if(!previewParameter.attentionModeOptions.arrival) {
            const container = document.querySelector('.heatmap-preview-arrival-container');
            if(container) container.innerHTML = '';  
          }

          if(!previewParameter.attentionModeOptions.arrivalTransition) {
            const container = document.querySelector('.heatmap-preview-arrival-transition-container');
            if(container) container.innerHTML = '';  
          }

          if(!previewParameter.attentionModeOptions.leave) {
            const container = document.querySelector('.heatmap-preview-leave-container');
            if(container) container.innerHTML = '';  
          }
          
          await renderHeatmap();
          return;
      }

      if(e.data.type === 'dataIdSelectMode') {
          isDataIdSelectModeEnabled = e.data.enabled;
          updateClickCountOverlay();
      }

      if(e.data.type === 'clickCountDisplay') {
          previewParameter.showClickCounts = e.data.enabled === true;
          updateClickCountOverlay();
      }
    });

    // Iframeで呼び出された場合の読み込み完了通知
    const previewReady = () => {
      // リスナー登録完了＝READY trueとみなす
      window.parent.postMessage({
        panelId: Number(panelId),
        type: 'lpIframeReady',
        isOk: true,
      }, '*');
    };
    previewReady();

    // ツールチップ操作
    document.addEventListener('mouseover', (e) => {
      // ツールチップ出現位置調整
      const calcTopY = (rect) => {
        const margin = 20;
        let y;
        if (rect.top > 0) {
          if (rect.bottom > windowHeight) {
            y = windowHeight - margin;
          } else {
            y = rect.top;
          }
        } else {
          y = margin;
        }

        // ちょっと調整
        if (y < margin) {
          y = margin;
        } else if (y > windowHeight - margin) {
          y = windowHeight - margin;
        }

        return y;
      };

      if(isDataIdSelectModeEnabled) {
        // データID選択モード
        let el = e.target.closest(`[${heatmapDomChangeDataIdKey}]`);
        if (!el) return;

        if(elHeatmapToolTip === null) elHeatmapToolTip = document.querySelector('.heatmap-tooltip');
        elHeatmapToolTip.textContent = el.getAttribute(heatmapDomChangeDataIdKey);
        elHeatmapToolTip.classList.add('is-show');
        
      } else {
        // クリックプレビューマウスオーバー時はクリック数を表示
        if(previewParameter.mode === 'click') {
          const el = e.target.closest(`.${heatmapClickClass}`);
          if (!el) return;
          if (Object.values(elementClickDatas).length === 0) return;
          const clickVal = elementClickDatas[el.getAttribute(heatmapDataIdKey)];
          if(!clickVal) return;

          if(elHeatmapToolTip === null) elHeatmapToolTip = document.querySelector('.heatmap-tooltip');
          if (!elHeatmapToolTip) return;
          const rect = el.getBoundingClientRect();
          const topY = calcTopY(rect);
          const centerX = rect.left + rect.width / 2;

          elHeatmapToolTip.style.left = centerX + 'px';
          elHeatmapToolTip.style.top = topY + 'px';
          elHeatmapToolTip.textContent = `${clickVal}クリック`;
          elHeatmapToolTip.classList.add('is-show');
        }
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (isDataIdSelectModeEnabled) {      
        let el = e.target.closest(`[${heatmapDomChangeDataIdKey}]`);
        if (!el) return;

        if(elHeatmapToolTip === null) elHeatmapToolTip = document.querySelector('.heatmap-tooltip');
        const margin = 20;
        const tooltipHeight = elHeatmapToolTip.offsetHeight || 0;
        let top = e.clientY + (tooltipHeight / 4);
        let left = e.clientX;

        // 画面端でのはみ出し防止
        if (top < margin) top = margin;
        if (top > windowHeight - margin) top = windowHeight - margin;
        if (left < margin) left = margin;
        if (left > windowWidth - margin) left = windowWidth - margin;

        elHeatmapToolTip.style.left = left + 'px';
        elHeatmapToolTip.style.top = top + 'px';
      }
    });

    document.addEventListener('mouseout', (e) => {
      if(elHeatmapToolTip === null) elHeatmapToolTip = document.querySelector('.heatmap-tooltip');
      if (!elHeatmapToolTip) return;
      elHeatmapToolTip.classList.remove('is-show');
    });

    document.addEventListener('click', (e) => {
      if(isDataIdSelectModeEnabled) {
        // データID選択モード
        let el = e.target.closest(`[${heatmapDomChangeDataIdKey}]`);
        if (!el) return;
        
        window.parent.postMessage({
          panelId: panelId,
          type: 'dataIdSelected',
          dataId: el.getAttribute(heatmapDomChangeDataIdKey),
        }, parentOrigin);  
      }
    });
}
